import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { Security } from "@mui/icons-material";
import { useUpdate, useNotify } from "react-admin";

interface RoleManagerProps {
  record: Record<string, unknown>;
  resource: string;
}

export const RoleManager: React.FC<RoleManagerProps> = ({
  record,
  resource,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [update] = useUpdate();
  const notify = useNotify();

  // Update selectedRoles when record changes
  useEffect(() => {
    if (record?.roles) {
      setSelectedRoles(
        (record.roles as Record<string, unknown>[]).map(
          (roleObj: Record<string, unknown>) =>
            (roleObj.role as string) || (roleObj as string),
        ),
      );
    }
  }, [record]);

  const roleOptions = [
    { value: "superAdmin", label: "Super Admin" },
    { value: "admin", label: "Admin" },
    { value: "support", label: "Support" },
    { value: "regular", label: "Regular" },
  ];

  const handleRoleChange = (event: { target: { value: string[] } }) => {
    setSelectedRoles(event.target.value);
  };

  const handleSave = async () => {
    if (!record?.id) return;

    try {
      await update(resource, {
        id: record.id,
        data: { roles: selectedRoles },
        meta: {
          endpoint: `${resource}/${record.id}/role`,
          method: "PUT",
        },
      });

      notify("User roles updated successfully", { type: "success" });
      setOpen(false);
    } catch {
      notify("Failed to update user roles", { type: "error" });
    }
  };

  // Don't render if no record
  if (!record) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<Security />}
        onClick={() => setOpen(true)}
        size="small"
        variant="outlined"
      >
        Manage Roles
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage User Roles</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Roles:
            </Typography>
            <Box sx={{ mb: 2 }}>
              {(record?.roles as Record<string, unknown>[])?.map(
                (roleObj: Record<string, unknown>, index: number) => (
                  <Chip
                    key={index}
                    label={roleObj.role || roleObj}
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ),
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Select Roles</InputLabel>
              <Select
                multiple
                value={selectedRoles}
                onChange={handleRoleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Roles
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
